import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "./utils/api";
import { useIdleTimer } from "./hooks/useIdleTimer";
import LockScreen from "./pages/LockScreen";
import { Toaster } from "react-hot-toast";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AdminDashboard from "./pages/admin/AdminDashboard";
import HRDashboard from "./pages/hr/HRDashboard";
import AccountsDashboard from "./pages/accounts/AccountsDashboard";
import ManagerDashboard from "./pages/manager/ManagerDashboard";
import Unauthorized from "./unauthorized";
import CreateRole from "./pages/roles/CreateRole";
import AssignPermissions from "./pages/roles/AssignPermissions";
import CreateUser from "./pages/admin/CreateUser";
import UserList from "./pages/admin/UserList";
import Modules from "./pages/modules/CreateModule";
import ModulesList from "./pages/modules/ModulesList";
import RoleList from "./pages/roles/RolesList";
import BoqList from "./pages/boq/BoqList";
import CreateBoq from "./pages/boq/CreateBoq";
import BoqView from "./pages/boq/BOQView";
import BoqItemsPage from "./pages/boq/CreateBoqItems";
import Projects from "./pages/projects/Projects";
import EditProject from "./pages/projects/EditProject";
import ViewProject from "./pages/projects/ProjectView";
import UserManual from "./pages/UserManual";
import ProtectedRoute from "./routes/ProtectedRoute";
import DashboardLayout from "./layouts/DashboardLayout";
import Client from "./pages/clients/Client";
import DcOutList from "./pages/dc/DcOutList";
import DcInPage from "./pages/dc/DcInPage";
import { logout } from "./auth/authService";
import VendorList from "./pages/vendors/VendorList";
import VendorView from "./pages/vendors/VendorView";
import VendorEdit from "./pages/vendors/VendorEdit";
import PurchaseOrderList from "./pages/purchaseOrders/PurchaseOrderList";
import CreatePurchaseOrder from "./pages/purchaseOrders/CreatePurchaseOrder";
import PurchaseOrderView from "./pages/purchaseOrders/PurchaseOrderView";
import PurchaseOrderEdit from "./pages/purchaseOrders/PurchaseOrderEdit";
import ChecklistList from "./pages/qa/checklist/ChecklistList";
import ChecklistView from "./pages/qa/checklist/ChecklistView";
import InspectionList from "./pages/qa/inspection/InspectionList";
import InspectionCreate from "./pages/qa/inspection/InspectionCreate";
import InspectionView from "./pages/qa/inspection/InspectionView";
import InspectionEdit from "./pages/qa/inspection/InspectionEdit";
import ChecklistItemsCreate from "./pages/qa/checklist/ChecklistItemsCreate";
import CreateNCR from "./pages/qa/ncr/CreateNCR";
import ListNCR from "./pages/qa/ncr/ListNCR";
import ViewNCR from "./pages/qa/ncr/ViewNCR";
import EditNCR from "./pages/qa/ncr/EditNCR";
import ModuleAuditPage from "./pages/qa/audit/ModuleAuditPage";
import ProjectAuditPage from "./pages/qa/audit/ProjectAuditPage";
import UserAuditPage from "./pages/qa/audit/UserAuditPage";
import UploadBoq from "./pages/upload";
import ProfilePage from "./pages/profile";
import SessionConflictModal from "./components/SessionConflictModal";
import { usePermissionModal } from "./context/PermissionModalContext";
import LowStock from "./pages/stocks/LowStock";
import StockDetails from "./pages/stocks/StockDetails";
import StockLedger from "./pages/stocks/StockLedger";
import StockList from "./pages/stocks/StockList";
import BillingDashboard from "./pages/billing/BillingDashboard";
import CreateBilling from "./pages/billing/CreateBilling";
import InvoiceView from "./pages/billing/InvoiceView";
import BoqAttachmentStatus from "./pages/boq/BoqAttachmentStatus";
import Payments from "./pages/billing/Payments";
import PurchaseOrderPayments from "./pages/purchaseOrders/PurchaseOrderPayments";
import LoginHistory from "./pages/LoginHistory";
import Sidebar from "./components/Sidebar";
import ProjectMembersList from "./pages/projects/ProjectMembersList";
import AttendanceManagement from "./pages/Attendance/Attendancemanaganet";
// import AttendanceList from "./pages/Attendance/AttendanceList";
import Invoice from "./pages/TaxInvoice/TaxInvoiceDashboard";

function App() {
  const [sessionConflict, setSessionConflict] = useState(false);
  const { setPermissionError } = usePermissionModal();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [locked, setLocked] = useState(
    localStorage.getItem("is_locked") === "true",
  );

  useEffect(() => {
    const handler = () => {
      setSessionConflict(true);
    };

    const permissionHandler = (e) => {
      setPermissionError(e.detail);
    };

    window.addEventListener("session-conflict", handler);
    window.addEventListener("permission-denied", permissionHandler);

    return () => {
      window.removeEventListener("session-conflict", handler);
      window.removeEventListener("permission-denied", permissionHandler);
    };
  }, [setPermissionError]);

  useEffect(() => {
    let interval;

    const checkSession = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        await api.get("/check-session");
      } catch (error) {
        if (error.response?.status === 409) {
          setSessionConflict(true);
          await api.post("/logout").catch(() => { });
          clearInterval(interval);
        }
      }
    };

    checkSession();

    interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        checkSession();
      }
    }, 60000);

    window.addEventListener("focus", checkSession);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", checkSession);
    };
  }, []);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleSessionLogout = async () => {
    try {
      const res = await api.post("/logout");
    } catch (e) {
      console.log("Logout API error:", e.response);
    }
    localStorage.clear();
    navigate("/login");
  };

  const handleUnlock = () => {
    setLocked(false);
    localStorage.removeItem("is_locked");
  };

  // ---------------- CHECK TOKEN ----------------
  // useEffect(() => {
  //   if (localStorage.getItem("token")) {
  //     fakeAccessApi().catch(() => logout());
  //   }
  // }, []);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <SessionConflictModal
        open={sessionConflict}
        onLogout={handleSessionLogout}
      />

      {locked && <LockScreen onUnlock={handleUnlock} />}
      {!locked && (
        <Routes>
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/upload" element={<UploadBoq />} />

          {/* ---------------- ADMIN ROUTES ---------------- */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute
                allowedRoles={["admin"]}
                requiredPermission="project.view"
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <AdminDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/create-role"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <CreateRole />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <RoleList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/modules"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <Modules />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/modules/list"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ModulesList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users/create"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <CreateUser />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* This shows the Table/List you shared */}
          <Route
            path="/admin/users/view"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <UserList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users/view/:id"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <UserList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/assignpermissions"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <AssignPermissions />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "manager", "hr", "stock", "accounts"]}
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ProfilePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- HR ---------------- */}
          <Route
            path="/hr"
            element={
              <ProtectedRoute allowedRoles={["hr", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <HRDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- STOCK ---------------- */}
          <Route
            path="/stock"
            element={
              <ProtectedRoute allowedRoles={["stock", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <StockList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/stock/:id"
            element={
              <ProtectedRoute allowedRoles={["stock", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <StockDetails />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/low-stock"
            element={
              <ProtectedRoute allowedRoles={["stock", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <LowStock />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/stock-ledger/:id"
            element={
              <ProtectedRoute allowedRoles={["stock", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <StockLedger />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- ACCOUNTS ---------------- */}
          <Route
            path="/accounts"
            element={
              <ProtectedRoute allowedRoles={["accounts", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <AccountsDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- MANAGER ---------------- */}
          <Route
            path="/manager"
            element={
              <ProtectedRoute allowedRoles={["manager", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ManagerDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- BOQ ---------------- */}
          <Route
            path="/boq"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <BoqList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/boqs/create"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <CreateBoq />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/boqs/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <BoqView />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/boqs/:id/items/create"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <BoqItemsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- PROJECTS ---------------- */}
          <Route
            path="/projects"
            element={
              <ProtectedRoute
                allowedRoles={["admin"]}
                requiredPermission="project.view"
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <Projects />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/edit/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <EditProject />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/view/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ViewProject />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- ATTENDANCE LIST (SELECT PROJECT) ---------------- */}
          {/* <Route
            path="/attendance"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "manager", "hr", "stock", "accounts"]}
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <AttendanceList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          /> */}

          {/* ---------------- PROJECT MEMBERS LIST ---------------- */}
          <Route
            path="/projects/:id/members"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "manager", "hr", "stock", "accounts"]}
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ProjectMembersList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- PROJECT ATTENDANCE MANAGEMENT ---------------- */}
          <Route
            path="/projects/:id/attendance"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "manager", "hr", "stock", "accounts"]}
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <AttendanceManagement />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/boq-attachment-status"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <BoqAttachmentStatus />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dc-out"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <DcOutList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dc-in"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <DcInPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/purchase-orders"
            element={
              <ProtectedRoute allowedRoles={["manager", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <PurchaseOrderList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/purchase-orders/create"
            element={
              <ProtectedRoute allowedRoles={["manager", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <CreatePurchaseOrder />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/purchase-orders/edit/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager", "stock"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <PurchaseOrderEdit />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/:id"
            element={
              <ProtectedRoute allowedRoles={["manager", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <PurchaseOrderView />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/po/:id/payments"
            element={
              <ProtectedRoute allowedRoles={["manager", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <PurchaseOrderPayments />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tax-invoices"
            element={
              <ProtectedRoute allowedRoles={["accounts", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <Invoice />
                </DashboardLayout>
              </ProtectedRoute>   
            }
          />

          <Route
            path="/tax-invoices/:id"
            element={
              <ProtectedRoute allowedRoles={["accounts", "admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <Invoice />
                </DashboardLayout>
              </ProtectedRoute>   
            }
          />

          {/* -------------------- QA Insepction NCR-------------------- */}
          <Route
            path="/inspections"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <InspectionList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/inspections/create"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <InspectionCreate />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/inspections/view/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <InspectionView />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/inspections/edit/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <InspectionEdit />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ncr"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ListNCR />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ncr/create"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <CreateNCR />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ncr/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ViewNCR />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ncr/edit/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <EditNCR />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Billing Route */}
          <Route
            path="/billing"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <BillingDashboard module="all" />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/billing/create-invoice"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <CreateBilling module="all" />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/billing/invoice/:id"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <InvoiceView module="all" />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment-details"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <Payments />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/audit/module"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ModuleAuditPage module="all" />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/audit/project"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ProjectAuditPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/audit/user"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <UserAuditPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/checklist"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ChecklistList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/checklists/view/:id"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ChecklistView />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/checklists/:id/items"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <ChecklistItemsCreate />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- CLIENTS ---------------- */}
          <Route
            path="/clients"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager", "hr"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <Client />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/view/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager", "hr"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <Client />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/edit/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager", "hr"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <Client />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/create"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager", "hr"]}>
                <DashboardLayout onLogout={handleSessionLogout}>
                  <Client />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Vendors Rourtes */}
          <Route
            path="/vendors"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "manager", "hr", "stock", "accounts"]}
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <VendorList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/vendors/view/:id"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "manager", "hr", "stock", "accounts"]}
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <VendorView />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/vendors/edit/:id"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "manager", "hr", "stock", "accounts"]}
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <VendorEdit />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* login history Rourtes */}
          <Route
            path="/admin/users/history/:id"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "manager", "hr", "stock", "accounts"]}
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <LoginHistory />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- USER MANUAL ---------------- */}
          <Route
            path="/user-manual"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "manager", "hr", "stock", "accounts"]}
              >
                <DashboardLayout onLogout={handleSessionLogout}>
                  <UserManual />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* ---------------- OTHERS ---------------- */}
          {/* <Route path="/unauthorized" element={<Unauthorized />} /> */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      )}
    </>
  );
}

export default App;
